/**
 * A moldura das folhas de impressão do RH, que o navegador salva como PDF.
 *
 * Papel: desenha o que as três folhas do módulo têm em comum — a
 * `.bh-print-page` da origem com o monograma e o logotipo Villa Prado, o título
 * serifado de 23px e a linha mono em latão — e, assim que monta, abre a janela
 * de impressão; `CaixaDaFolha` é a `.bh-print-box`, a caixa com o título sobre
 * o fundo `superficie-folha`.
 *
 * Como usar: montar a folha é o que imprime, e por isso quem a monta só o faz
 * no clique de imprimir, com uma `key` nova a cada clique, e a desmonta quando
 * `onConcluida` é chamado, depois que a janela de impressão fecha — a impressão
 * é de `useImpressaoAoMontar`, que imprime uma vez por montagem. Deixá-la
 * montada faria o Ctrl+P seguinte, dado na tela, sair com a folha no lugar da
 * tela. Enquanto ela está montada, quem a monta também deverá esconder da
 * impressão todo o resto da página (`print:hidden`), senão o papel sai com a
 * tela inteira e a folha depois dela.
 *
 * Na tela a folha nunca aparece (`hidden`): ela só existe para a mídia de
 * impressão (`print:block`). As imagens da marca vêm de `public/marca/`, e a
 * janela de impressão só abre depois que as duas terminam de carregar — aberta
 * antes, a folha sairia sem a marca no topo.
 *
 * Quando uma tabela passa da largura do papel, a folha inteira cresce com ela
 * (`w-fit`) e o navegador reduz a página para caber; a caixa não recorta
 * (`overflow-hidden` faria colunas sumirem do PDF sem aviso).
 *
 * O `<main>` da moldura tem 32 px de respiro nas laterais, e a folha os desfaz
 * com `print:-mx-8` e os devolve à largura mínima com
 * `print:min-w-[calc(100%+4rem)]`: a margem do papel é a da página nomeada
 * `folha-retrato`, declarada em `index.css`, mais o respiro interno da própria
 * folha. Se o respiro do `<main>` mudar, as duas compensações terão de mudar
 * junto.
 */
import { type ReactNode } from 'react';

import { useImpressaoAoMontar } from '@/shared/hooks';
import { cn } from '@/shared/lib/utils';

interface FolhaDeImpressaoProps {
  /** O título da folha, como `.bh-print-title`. */
  titulo: string;
  /** A linha mono em latão, como `.bh-print-sub`; sai em caixa alta. */
  subtitulo: string;
  /** Chamado quando a janela de impressão fecha, impressa ou cancelada. */
  onConcluida: () => void;
  /** As caixas da folha. */
  children: ReactNode;
}

/**
 * A folha, que abre a janela de impressão ao montar.
 *
 * @param titulo - O título serifado.
 * @param subtitulo - A linha abaixo do título.
 * @param onConcluida - Chamado uma vez, no `afterprint`.
 * @param children - As caixas.
 * @returns A folha, escondida na tela e visível só na impressão.
 */
export function FolhaDeImpressao({
  titulo,
  subtitulo,
  onConcluida,
  children,
}: FolhaDeImpressaoProps) {
  const refDaFolha = useImpressaoAoMontar<HTMLDivElement>(onConcluida);

  return (
    <div
      ref={refDaFolha}
      className="hidden w-fit bg-superficie p-[20mm_18mm] font-sans text-foreground [page:folha-retrato] [print-color-adjust:exact] print:-mx-8 print:block print:min-w-[calc(100%+4rem)]"
    >
      <div className="mb-7 flex items-center gap-4 border-b border-linha-forte pb-[18px]">
        <img
          src="/marca/villa-prado-monograma.png"
          alt="Villa Prado"
          width={36}
          height={36}
          className="size-[34px] shrink-0 object-contain"
        />
        <img
          src="/marca/villa-prado-logotipo.png"
          alt="Villa Prado"
          className="h-[34px] w-auto object-contain"
        />
      </div>

      <h1 className="mb-1.5 font-serif text-[23px] font-medium text-foreground">{titulo}</h1>
      <p className="mb-[26px] font-mono text-[10.5px] tracking-[0.08em] text-brass uppercase">
        {subtitulo}
      </p>

      {children}
    </div>
  );
}

interface CaixaDaFolhaProps {
  /** O título da caixa: um texto, ou o bloco do colaborador com o saldo. */
  titulo: ReactNode;
  /** Classes de quem chama, como a margem entre duas caixas. */
  className?: string;
  /** A tabela da caixa; sem ela, a caixa é só o título. */
  children?: ReactNode;
}

/**
 * Uma caixa da folha, com o título sobre o fundo claro.
 *
 * @param titulo - O título da caixa.
 * @param className - Classes de quem chama, aplicadas depois das internas.
 * @param children - A tabela.
 * @returns A caixa com borda e cantos arredondados.
 */
export function CaixaDaFolha({ titulo, className, children }: CaixaDaFolhaProps) {
  return (
    <div className={cn('rounded-md border border-linha-forte', className)}>
      <div className="rounded-t-[5px] border-b border-linha bg-superficie-folha px-5 py-[15px] font-serif text-[15px] font-medium text-foreground">
        {titulo}
      </div>
      {children}
    </div>
  );
}
